import { validateVatClassification } from '../../utils/vatClassification';
import {
  SERVICE_PACKAGE_MAX_EXPIRES_IN_MONTHS,
  SERVICE_PACKAGE_MAX_QUANTITY,
} from '../../services/servicePackageService';

const INTEGER_PATTERN = /^\d+$/;

const isBlank = (value) => value === '' || value == null || String(value).trim() === '';

const parseStrictInteger = (value) => {
  const text = String(value).trim();
  return INTEGER_PATTERN.test(text) ? Number.parseInt(text, 10) : Number.NaN;
};

const hasAtMostTwoDecimals = (value) => Math.abs(Math.round(value * 100) - (value * 100)) < 1e-6;

const isIntegerInRange = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

/**
 * Validates the service package form. Errors are i18n keys, so they follow language switches.
 * Returns the errors and, when valid, the parsed numeric values ready to save.
 */
export const validateServicePackageForm = (formData, supportedLanguages = []) => {
  const errors = {};
  const languages = supportedLanguages.length > 0 ? supportedLanguages : ['en'];

  if (!languages.every((language) => formData?.name?.[language]?.trim())) {
    errors.name = 'servicePackagesManagement.validation.nameRequired';
  }

  const price = isBlank(formData?.price) ? Number.NaN : Number(formData.price);
  if (!Number.isFinite(price) || price <= 0) {
    errors.price = 'servicePackagesManagement.validation.priceRequired';
  } else if (!hasAtMostTwoDecimals(price)) {
    errors.price = 'servicePackagesManagement.validation.priceDecimals';
  }

  const quantity = isBlank(formData?.quantity) ? Number.NaN : parseStrictInteger(formData.quantity);
  if (!isIntegerInRange(quantity, 1, SERVICE_PACKAGE_MAX_QUANTITY)) {
    errors.quantity = 'servicePackagesManagement.validation.quantityRequired';
  }

  const expiresInMonths = isBlank(formData?.expiresInMonths) ? null : parseStrictInteger(formData.expiresInMonths);
  if (expiresInMonths != null && !isIntegerInRange(expiresInMonths, 1, SERVICE_PACKAGE_MAX_EXPIRES_IN_MONTHS)) {
    errors.expiresInMonths = 'servicePackagesManagement.validation.expiresInMonthsInvalid';
  }

  if (!Array.isArray(formData?.serviceIds) || formData.serviceIds.length === 0) {
    errors.serviceIds = 'servicePackagesManagement.validation.servicesRequired';
  }

  Object.assign(errors, validateVatClassification(formData));

  const isValid = Object.keys(errors).length === 0;

  return {
    errors,
    isValid,
    values: isValid ? { price, quantity, expiresInMonths } : null,
  };
};

export default validateServicePackageForm;
